import {
  Component,
  OnInit,
  Input,
  ContentChild,
  ElementRef,
  ChangeDetectorRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ContentChildren,
  QueryList,
} from '@angular/core';
import { BllFormFieldControl } from './form-field-control';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { NgControl } from '@angular/forms';
import { BllError } from './error';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

let nextUniqueId = 0;
@Component({
  selector: 'bll-form-field',
  templateUrl: './form-field.component.html',
  host: {
    class: 'bll-form-field',
    '[class.bll-form-field-invalid]': '_checkError()',
    '[class.bll-form-field-inline]': 'inline',
    '[class.bll-form-field-width]': 'width',
    '[style.width.%]': 'width',
    '[class.bll-form-field-hide-placeholder]': 'hidePlaceholder',
    '[class.bll-form-field-disabled]': '_control.disabled',
    '[class.bll-focused]': '_control.focused',
    '[class.bll-form-field-active]': '_control.shouldLabelFloat',
    '[class.ng-untouched]': '_shouldForward("untouched")',
    '[class.ng-touched]': '_shouldForward("touched")',
    '[class.ng-pristine]': '_shouldForward("pristine")',
    '[class.ng-dirty]': '_shouldForward("dirty")',
    '[class.ng-valid]': '_shouldForward("valid")',
    '[class.ng-invalid]': '_shouldForward("invalid")',
    '[class.ng-pending]': '_shouldForward("pending")',
  },
  styleUrls: ['./form-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BllFormField implements OnInit {
  private _destroyed = new Subject<void>();

  // Unique id for the internal form field label.
  _labelId = `bll-form-field-label-${nextUniqueId++}`;

  @ContentChildren(BllError) _errorChildren: QueryList<BllError>;
  @ContentChild(BllFormFieldControl, { static: true })
  _control: BllFormFieldControl<any>;
  @Input() hidePlaceholder: boolean = true;
  @Input() hint: string | null = null;
  @Input() error: boolean = false;

  /** Whether the required marker should be hidden. */
  @Input()
  get hideRequiredMarker(): any {
    return this._hideRequiredMarker;
  }
  set hideRequiredMarker(value: any) {
    this._hideRequiredMarker = value;
  }
  private _hideRequiredMarker: boolean;

  @Input()
  get inline(): any {
    return this._dInline;
  }
  set inline(value: any) {
    this._dInline = coerceBooleanProperty(value);
  }
  private _dInline: boolean;

  @Input()
  get clearable(): any {
    return this._clearable;
  }
  set clearable(value: any) {
    this._clearable = coerceBooleanProperty(value);
  }
  private _clearable: boolean;

  // should be percent >0
  @Input()
  get width(): number | string {
    return this._width;
  }
  set width(value: number | string) {
    if (value === 'full') value = 100;
    value = Number(value);
    if (typeof value === 'number' && value >= 0) {
      this._width = value;
    } else {
      throw new Error("bll-form-field width must be number greater then 0 or 'full'");
    }
  }
  private _width: number;

  @Input()
  get currency(): string {
    return this._currency;
  }
  set currency(value: string) {
    const lower = value.toLocaleLowerCase();
    if (lower === 'ils' || lower === 'nis') {
      value = '₪';
    } else if (lower === 'dollar' || lower === 'dol') {
      value = '$';
    }
    this._currency = value;
  }
  private _currency: string;

  @Input()
  get prefix(): string {
    return this._prefix;
  }
  set prefix(value: string) {
    const lower = value.toLocaleLowerCase();
    if (lower === 'percent') {
      value = '%';
    }
    this._prefix = value;
  }
  private _prefix: string | null = null;

  constructor(public _elementRef: ElementRef, private _changeDetectorRef: ChangeDetectorRef) {}

  ngAfterContentInit() {
    const control = this._control;
    if (control.controlType) {
      this._elementRef.nativeElement.classList.add(`bll-form-field-type-${control.controlType}`);
    }

    // Subscribe to changes in the child control state in order to update the form field UI.
    control.stateChanges.pipe(startWith(null!)).subscribe(() => {
      this._changeDetectorRef.markForCheck();
    });

    // Run change detection if the value changes.
    if (control.ngControl && control.ngControl.valueChanges) {
      control.ngControl.valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => this._changeDetectorRef.markForCheck());
    }

    this._errorChildren.changes.pipe(startWith(null)).subscribe(() => {
      this._changeDetectorRef.markForCheck();
    });

    /** TODO: Remove this in favor of angular/issues/10887 when PR got ready */
    const formControl = control.ngControl?.control;
    if (formControl) {
      const methotdsToPatch = ['markAsTouched', 'markAllAsTouched', 'markAsUntouched', 'markAsDiry', 'markAsPristine'];
      methotdsToPatch.forEach((method) => {
        if (formControl[method]) {
          const oldFn = formControl[method].bind(formControl);
          formControl[method] = (...params) => {
            oldFn(params);
            this._changeDetectorRef.markForCheck();
          };
        }
      });
    }
  }

  _checkError(): boolean {
    return (
      this._control.errorState ||
      (this._control.ngControl && this._control.ngControl.errors !== null && !!this._control.ngControl.touched) ||
      this.error
    );
  }

  ngOnInit() {}

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }
  /** Determines whether a class from the NgControl should be forwarded to the host element. */
  _shouldForward(prop: keyof NgControl): boolean {
    const ngControl = this._control ? this._control.ngControl : null;
    return ngControl && ngControl[prop];
  }

  _hasPlaceholder() {
    return !!(this._control && this._control.placeholder);
  }

  _hasLabel() {
    return false; //@todo change
    // return !!this._labelChild;
  }
  /** Determines whether to display hints or errors. */
  _displayError(): boolean {
    return this._errorChildren && this._errorChildren.length > 0 && this._checkError() ? true : false;
  }

  get value() {
    return this._control?.ngControl.value;
  }

  clear() {
    this._control?.ngControl?.reset();
  }
}
